import { Mail, Phone, Globe } from "lucide-react";
import { useSEO, webPageSchema, breadcrumbSchema } from "@/lib/seo";
import { PAGE_META, CAPABILITY } from "@/data/page_meta";
import { CONTACT } from "@/data/site";

const M = PAGE_META.capability;

const Heading = ({ children }) => (
  <h2 className="font-heading text-xl sm:text-2xl font-semibold text-navy mb-4 pb-2 border-b-2 border-leaf/60">{children}</h2>
);

const CompanyData = () => (
  <section data-testid="cap-company-data">
    <Heading>Company Data</Heading>
    <dl className="space-y-3 text-base">
      {CAPABILITY.data.map(([k, v]) => (
        <div key={k}>
          <dt className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{k}</dt>
          <dd className="font-medium text-navy" data-testid={`cap-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{v}</dd>
        </div>
      ))}
      <div>
        <dt className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Service Area</dt>
        <dd className="font-medium text-navy">{CAPABILITY.areas.map((a) => <span key={a} className="block">{a}</span>)}</dd>
      </div>
    </dl>
  </section>
);

const Certifications = () => (
  <section data-testid="cap-certifications">
    <Heading>Certifications</Heading>
    <ul className="grid grid-cols-2 gap-4">
      {CAPABILITY.certs.map((c) => (
        <li key={c.name} className={`rounded-2xl border border-border bg-white p-4 flex flex-col items-center text-center ${c.wide ? "col-span-2" : ""}`}>
          <img src={c.img} alt={c.alt} loading="lazy" className={`${c.wide ? "h-20" : "h-28"} w-auto object-contain`} data-testid={`cap-badge-${c.id}`} />
          <span className="mt-3 text-sm font-medium text-navy">{c.name}</span>
        </li>
      ))}
    </ul>
  </section>
);

const Overview = () => (
  <>
    <section data-testid="cap-overview">
      <Heading>Company Overview</Heading>
      <p className="text-base sm:text-lg text-foreground/80 leading-relaxed">{CAPABILITY.overview}</p>
    </section>
    <section data-testid="cap-competencies">
      <Heading>Core Competencies</Heading>
      <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
        {CAPABILITY.competencies.map((c) => (
          <li key={c} className="flex gap-2 text-base text-foreground/80"><span className="mt-2 w-1.5 h-1.5 rounded-full bg-leaf shrink-0" />{c}</li>
        ))}
      </ul>
    </section>
    <section data-testid="cap-differentiators">
      <Heading>Differentiators</Heading>
      <div className="space-y-4">
        {CAPABILITY.differentiators.map(([t, d]) => (
          <p key={t} className="text-base text-foreground/80 leading-relaxed"><strong className="text-navy">{t}</strong> {d}</p>
        ))}
      </div>
    </section>
  </>
);

export default function CapabilityStatement() {
  useSEO({
    ...M,
    jsonLd: [
      webPageSchema({ path: M.path, name: M.title, description: M.description }),
      breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Capability Statement", path: M.path }]),
    ],
  });
  return (
    <div className="pt-28 pb-20 bg-muted/30" data-testid="capability-page">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <header className="rounded-t-3xl bg-navy text-white px-6 sm:px-10 py-8 sm:py-10 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-leaf mb-2">Capability Statement</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight" data-testid="cap-title">CJB Clean</h1>
            <p className="mt-2 text-white/80">Woman-Owned Small Business</p>
          </div>
          <p className="font-heading text-lg text-white/90 md:text-right leading-snug">Healthy Spaces.<br />Smarter Solutions.<br />Reliable Service.</p>
        </header>
        <div className="bg-white border-x border-border px-6 sm:px-10 py-10 grid md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-10">
          <aside className="space-y-10"><CompanyData /><Certifications /></aside>
          <div className="space-y-10"><Overview /></div>
        </div>
        <footer className="rounded-b-3xl bg-navy text-white px-6 sm:px-10 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm">
          <span data-testid="cap-footer-ids">CJB Clean · UEI: {CAPABILITY.uei} · CAGE: {CAPABILITY.cage}</span>
          <span className="flex flex-wrap gap-x-5 gap-y-1">
            <a href={CONTACT.phoneHref} className="inline-flex items-center gap-1.5 hover:text-leaf" data-testid="cap-phone"><Phone className="w-4 h-4" />{CONTACT.phone}</a>
            <a href={`mailto:${CONTACT.email}`} className="inline-flex items-center gap-1.5 hover:text-leaf" data-testid="cap-email"><Mail className="w-4 h-4" />{CONTACT.email}</a>
            <span className="inline-flex items-center gap-1.5"><Globe className="w-4 h-4" />cjbclean.com</span>
          </span>
        </footer>
      </div>
    </div>
  );
}
